import * as React from 'react';
import {
  EAttribute_InputType,
  MemberDirectoryAttributeFilterInput,
  MemberDirectorySearchInput,
} from '@/graphql-types/globalTypes';
import { Filter, getFilterFacetValues } from '@/hooks/useFilterableAttributes';
import { MEMBER_DIRECTORY_STATE_FILTER_NAME } from '../constants';
import { MemberDirectoryFilterOptions } from '../types';

const buildAttributeSchemas = (
  filterOptions: MemberDirectoryFilterOptions | undefined
) =>
  filterOptions?.attributeFilters.map((schema) => ({
    _id: schema._id,
    name: schema.name,
    inputType: schema.inputType,
    options: schema.options.map((option) => ({
      id: option.label,
      label: option.label,
    })),
  })) ?? [];

export const isFreeTextDirectoryFilter = (filter: Filter): boolean =>
  filter.attributeSchema.inputType === EAttribute_InputType.FREE_TEXT;

export const isStateDirectoryFilter = (filter: Filter): boolean =>
  filter.attributeSchema.name === MEMBER_DIRECTORY_STATE_FILTER_NAME;

export const buildMemberDirectorySearchFilters = (
  selectedFilters: Filter[],
  freeTextValues: Record<string, string>
): Pick<MemberDirectorySearchInput, 'attributeFilters' | 'stateSearchText'> => {
  const attributeFilters: MemberDirectoryAttributeFilterInput[] = [];
  let stateSearchText: string | null = null;

  selectedFilters.forEach((filter) => {
    const { attributeSchema } = filter;
    if (isFreeTextDirectoryFilter(filter)) {
      const text = freeTextValues[attributeSchema._id]?.trim() ?? '';
      if (!text) {
        return;
      }

      // Legacy State address filter still uses dedicated search input field.
      if (attributeSchema.name === MEMBER_DIRECTORY_STATE_FILTER_NAME) {
        stateSearchText = text;
        return;
      }

      attributeFilters.push({
        attributeSchemaId: attributeSchema._id,
        value: text,
      });
      return;
    }

    getFilterFacetValues(filter).forEach((value) => {
      attributeFilters.push({
        attributeSchemaId: attributeSchema._id,
        value,
      });
    });
  });

  return {
    attributeFilters: attributeFilters.length > 0 ? attributeFilters : null,
    stateSearchText,
  };
};

export const useMemberDirectoryFilters = (
  filterOptions: MemberDirectoryFilterOptions | undefined,
  isLoadingOptions: boolean
) => {
  const attributeSchemas = React.useMemo(
    () => buildAttributeSchemas(filterOptions),
    [filterOptions]
  );

  const [_selectedFilters, setSelectedFilters] = React.useState<Filter[]>([]);

  const selectFilter = React.useCallback(
    (params: { attributeSchemaId: string; facet?: string; facets?: string[] }): void => {
      const attributeSchema = attributeSchemas.find(
        (schema) => schema._id === params.attributeSchemaId
      );

      if (attributeSchema) {
        setSelectedFilters((previousValue) => [
          ...previousValue.filter(
            (filter) => filter.attributeSchema._id !== params.attributeSchemaId
          ),
          {
            attributeSchema,
            facet: params.facet,
            ...(params.facets ? { facets: params.facets } : {}),
          },
        ]);
      }
    },
    [attributeSchemas]
  );

  const unselectFilter = React.useCallback(
    (params: { attributeSchemaId: string }) => {
      setSelectedFilters((previousValue) =>
        previousValue.filter(
          (filter) => filter.attributeSchema._id !== params.attributeSchemaId
        )
      );
    },
    []
  );

  const unselectedFilters: Filter[] = React.useMemo(() => {
    return attributeSchemas
      .filter(
        (attributeSchema) =>
          !_selectedFilters
            .map((filter) => filter.attributeSchema._id)
            .includes(attributeSchema._id)
      )
      .map((attributeSchema) => ({ attributeSchema, facet: undefined }));
  }, [attributeSchemas, _selectedFilters]);

  const selectedFilters: Filter[] = React.useMemo(() => {
    return [..._selectedFilters].sort((a, b) => {
      if (a.attributeSchema.name < b.attributeSchema.name) {
        return -1;
      }
      if (a.attributeSchema.name > b.attributeSchema.name) {
        return 1;
      }
      return 0;
    });
  }, [_selectedFilters]);

  const someSelectFilterApplied = selectedFilters.some(
    (filter) =>
      !isFreeTextDirectoryFilter(filter) &&
      getFilterFacetValues(filter).length > 0
  );

  return {
    selectedFilters,
    unselectedFilters,
    selectFilter,
    unselectFilter,
    isLoadingFilters: isLoadingOptions,
    someSelectFilterApplied,
  };
};
